import React, { useMemo, type ReactNode } from 'react';
import { View, StyleSheet } from 'react-native';
import { FormDropdown } from './FormDropdown';
import { FormFieldLabelWithInfo } from './FormFieldLabelWithInfo';
import { formFieldInStack } from './SubsectionForm';
import type { ComponentProps } from 'react';

type FormDropdownBaseProps = ComponentProps<typeof FormDropdown>;

const styles = StyleSheet.create({
  field: {
    width: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
  },
});

type FormDropdownFieldProps = FormDropdownBaseProps & {
  label: ReactNode;
} & {
  onInfo?: () => void;
  infoA11yHint?: string;
  a11yDescription?: string;
};

/**
 * `FormDropdown` with optional (i) help: same pattern as **Project information** (label + icon row,
 * then `label=""` on the control). Prefer this over separate `FormFieldLabelWithInfo` + `FormDropdown`
 * at call sites.
 */
export const FormDropdownField: React.FC<FormDropdownFieldProps> = (props) => {
  const {
    label,
    required,
    containerStyle,
    onInfo,
    infoA11yHint,
    a11yDescription,
    ...dropdownProps
  } = props;
  const mergedContainer = useMemo(
    () => [formFieldInStack, containerStyle] as const,
    [containerStyle],
  );

  if (onInfo != null && infoA11yHint != null && a11yDescription != null) {
    return (
      <View style={styles.field}>
        <FormFieldLabelWithInfo
          onInfo={onInfo}
          infoA11yHint={infoA11yHint}
          a11yDescription={a11yDescription}
          required={required}
        >
          {label}
        </FormFieldLabelWithInfo>
        <FormDropdown
          {...dropdownProps}
          label=""
          required={!!required}
          containerStyle={mergedContainer}
        />
      </View>
    );
  }

  return (
    <FormDropdown
      {...dropdownProps}
      label={typeof label === 'string' ? label : ''}
      required={!!required}
      containerStyle={containerStyle}
    />
  );
};

FormDropdownField.displayName = 'FormDropdownField';

export type { FormDropdownFieldProps };
